import { FetchError, throwOnError, useAuth } from 'prove-common'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// Hook to get the current user (includes labId for lab users)
export const useCurrentUserQuery = () => {
  const { authFetch } = useAuth()
  return useQuery({
    queryKey: ['currentUser'],
    retry: false,
    queryFn: async () => {
      const { data } = await authFetch().GET('/users/me')
      return data ?? null
    }
  })
}

export const useGetLabNotificationEmailsQuery = () => {
  const { authFetch } = useAuth()
  const { data: currentUser } = useCurrentUserQuery()
  const labId = currentUser?.labId

  return useQuery({
    queryKey: ['labNotificationEmails', labId],
    retry: false,
    enabled: labId != null, // Only run query if we have a lab ID
    queryFn: async () => {
      if (labId == null) return null
      try {
        const { data } = await authFetch().GET(
          '/labs/{labId}/notification-emails',
          {
            params: { path: { labId } }
          }
        )
        return data ?? null
      } catch (err) {
        if (err instanceof FetchError && err.status === 404) return null
        throw err
      }
    }
  })
}

export const useAddLabNotificationEmailMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  const { data: currentUser } = useCurrentUserQuery()
  const labId = currentUser?.labId

  return useMutation({
    mutationFn: async (email: string) => {
      if (labId == null) throw new Error('Lab ID not available')
      const { error } = await authFetch().POST(
        '/labs/{labId}/notification-emails',
        {
          params: { path: { labId } },
          body: { email }
        }
      )
      if (error) {
        throwOnError(error)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['labNotificationEmails', labId]
      })
    }
  })
}

export const useRemoveLabNotificationEmailMutation = () => {
  const queryClient = useQueryClient()
  const { authFetch } = useAuth()
  const { data: currentUser } = useCurrentUserQuery()
  const labId = currentUser?.labId

  return useMutation({
    mutationFn: async (email: string) => {
      if (labId == null) throw new Error('Lab ID not available')
      const { error } = await authFetch().DELETE(
        '/labs/{labId}/notification-emails/{email}',
        {
          params: { path: { labId, email } }
        }
      )
      if (error) {
        throwOnError(error)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['labNotificationEmails', labId]
      })
    }
  })
}
